// Painel "Onde você quer receber?".
//
// Um componente, duas origens de acionamento (o seletor da barra superior e o link "alterar"
// do card de entrega) e dois formatos, decididos por LARGURA DE JANELA:
//
//   >= 768px  popover ancorado, NÃO modal — sem role="dialog", sem prisão de foco
//   <  768px  painel inferior, diálogo modal — role="dialog", prisão de foco, foco devolvido
//
// A semântica difere de propósito: um popover que prende o foco é uma armadilha de teclado,
// e um painel inferior que não prende deixa o leitor de tela vagando pela página atrás.
//
// Renderizado em portal no document.body. Sem isso ele nasce atrás do header — ver o
// diagnóstico em styles.js.
//
// Correções deliberadas sobre o protótipo (§9 da especificação visual): Enter aplica; o erro
// muda a borda do campo além da mensagem, e a mensagem é associada ao campo; foco visível por
// teclado; painel inferior com altura máxima e lista rolando por dentro; áreas de toque de
// 44px; mesmo fundo nas duas plataformas; item "CEP informado" no topo da lista quando o
// contexto é CEP avulso.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import MaskedInput from "react-text-mask";
import { FiX, FiHome } from "react-icons/fi";

import useAnchoredPosition from "../../hooks/useAnchoredPosition";
import useMediaQuery from "../../hooks/useMediaQuery";
import { Creators as DeliveryContextActions, Origem } from "../../store/ducks/deliveryContext";
import { Creators as AddressActions } from "../../store/ducks/address";
import { getAddressByCEPCached } from "../../services/cep";
import { normalizeCep } from "../../util/deliveryContextStorage";
import { formatCEP } from "../../util/formatCEP";
import { formatAddress } from "../../util/formatAddress";
import { cepMask } from "../../util/mask";
import SelectStoreModal from "../SelectStoreModal";
import {
  Backdrop,
  Painel,
  Cabecalho,
  Titulo,
  BotaoFechar,
  LinhaExplicativa,
  ListaEnderecos,
  ItemEndereco,
  Radio,
  ItemTextos,
  ItemTitulo,
  ItemResumo,
  DivisorOu,
  CampoBloco,
  RotuloCampo,
  LinhaCampo,
  InputCep,
  BotaoAplicar,
  MensagemErro,
  RodapePainel,
  LinkFarmacia,
  RegiaoStatus
} from "./styles";

const BOTTOM_SHEET_QUERY = "(max-width: 768px)";
const DEBOUNCE_LOOKUP = 500;

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export default function DeliveryLocationPanel({
  anchorRef,
  align = "left",
  // O painel e montado em dois lugares (seletor da barra e link "alterar" do card). Se os
  // dois abrirem, um id fixo se duplicaria no documento e o aria-controls apontaria para o
  // painel errado.
  id = "painel-entrega",
  onClose
}) {
  const dispatch = useDispatch();
  const painelRef = useRef(null);
  const inputRef = useRef(null);
  const primeiroItemRef = useRef(null);
  const debounceRef = useRef(null);

  const ehPainelInferior = useMediaQuery(BOTTOM_SHEET_QUERY);
  const posicao = useAnchoredPosition(anchorRef, painelRef, { align });

  const { cep, origem, enderecoId, erroCep } = useSelector((state) => state.deliveryContext);
  const signed = useSelector((state) => state.auth.signed);
  const enderecos = useSelector((state) => state.address.data);

  const [valorCep, setValorCep] = useState(() => formatCEP(cep) || "");
  const [aplicando, setAplicando] = useState(false);
  const [anuncio, setAnuncio] = useState("");
  const [modalFarmaciaAberto, setModalFarmaciaAberto] = useState(false);
  // O modal de farmácia fica fora do painel: com ele aberto, clique "fora" do painel não fecha (fecharia o modal junto)
  const modalFarmaciaAbertoRef = useRef(false);
  modalFarmaciaAbertoRef.current = modalFarmaciaAberto;

  const cepAvulsoAtivo = origem === Origem.MANUAL_CEP && !!cep;

  // Lista só existe para cliente identificado. GET_ADDRESSES_REQUEST zera `data` enquanto
  // busca, então a própria lista vazia é o estado de carregamento.
  useEffect(() => {
    if (signed) dispatch(AddressActions.getAddressesRequest());
  }, [signed, dispatch]);

  // Erro antigo não pode reaparecer numa abertura seguinte.
  useEffect(() => {
    dispatch(DeliveryContextActions.clearCepError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fechar = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onClose();
  }, [onClose]);

  // Teclado e clique fora. O clique fora ignora o gatilho: sem isso o mesmo gesto fecharia e
  // reabriria, porque o gatilho alterna o estado.
  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        // Com o modal de farmácia aberto, o Esc fecha só o modal
        if (modalFarmaciaAbertoRef.current) setModalFarmaciaAberto(false);
        else fechar();
        return;
      }

      // Prisão de foco só no painel inferior, que é modal. No popover o Tab deve sair.
      if (event.key === "Tab" && ehPainelInferior && painelRef.current) {
        const focaveis = painelRef.current.querySelectorAll(FOCUSABLE);
        if (focaveis.length === 0) return;

        const primeiro = focaveis[0];
        const ultimo = focaveis[focaveis.length - 1];

        if (event.shiftKey && document.activeElement === primeiro) {
          event.preventDefault();
          ultimo.focus();
        } else if (!event.shiftKey && document.activeElement === ultimo) {
          event.preventDefault();
          primeiro.focus();
        }
      }
    }

    function onPointerDown(event) {
      if (modalFarmaciaAbertoRef.current) return;
      const dentro = painelRef.current && painelRef.current.contains(event.target);
      const noGatilho = anchorRef.current && anchorRef.current.contains(event.target);
      if (!dentro && !noGatilho) fechar();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [anchorRef, fechar, ehPainelInferior]);

  // Foco ao abrir e devolução ao fechar. A devolução é exigência do formato modal, mas fazê-la
  // nos dois formatos é o comportamento correto para quem navega por teclado no desktop.
  useEffect(() => {
    const gatilho = anchorRef.current;

    const alvo = signed && primeiroItemRef.current ? primeiroItemRef.current : inputRef.current;
    if (alvo) alvo.focus();

    return () => {
      if (gatilho && typeof gatilho.focus === "function") gatilho.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trava de rolagem do painel inferior.
  //
  // NOTA DE FRAGILIDADE: `document.body.style.overflow` tem dois donos. TopbarIdentidadeVisual
  // também escreve nela e a devolve para "auto" fixo. Aqui salvamos e restauramos o valor
  // anterior, o que resolve a disputa hoje, mas duas propriedades globais compartilhadas
  // voltam a colidir na próxima vez que alguém abrir um overlay. A saída definitiva é um dono
  // único (um contexto ou um contador de travas), fora do escopo desta sessão.
  useEffect(() => {
    if (!ehPainelInferior) return undefined;

    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = anterior;
    };
  }, [ehPainelInferior]);

  // Aquece o cache do CEP enquanto o cliente digita, para que "Aplicar" responda na hora.
  // O debounce vive aqui, no campo; a memoização vive em services/cep.js.
  function onChangeCep(event) {
    const valor = event.target.value;
    setValorCep(valor);

    if (erroCep) dispatch(DeliveryContextActions.clearCepError());

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const digitos = normalizeCep(valor);
    if (!digitos) return;

    debounceRef.current = setTimeout(() => {
      getAddressByCEPCached(digitos).catch(() => {});
    }, DEBOUNCE_LOOKUP);
  }

  async function aplicarCep() {
    const digitos = normalizeCep(valorCep);

    if (!digitos) {
      dispatch(DeliveryContextActions.cepInvalido("Informe um CEP com 8 dígitos."));
      return;
    }

    setAplicando(true);

    try {
      const { data } = await getAddressByCEPCached(digitos);

      if (!data || data.erro) {
        dispatch(DeliveryContextActions.cepInvalido());
        return;
      }

      dispatch(
        DeliveryContextActions.applyCep(digitos, {
          cidade: data.localidade || null,
          uf: data.uf || null,
          bairro: data.bairro || null
        })
      );

      setAnuncio(`Entrega atualizada para ${formatCEP(digitos)}`);
      fechar();
    } catch (_) {
      dispatch(DeliveryContextActions.cepInvalido("Não foi possível consultar o CEP."));
    } finally {
      setAplicando(false);
    }
  }

  // Enter equivale a acionar o botão. No protótipo não fazia nada.
  function onKeyDownCep(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      aplicarCep();
    }
  }

  function selecionarEndereco(endereco) {
    dispatch(
      DeliveryContextActions.selectAddress(endereco.id, normalizeCep(endereco.cep), {
        cidade: endereco.cidade?.nome || null,
        uf: endereco.cidade?.uf_sigla || null,
        bairro: endereco.bairro || null
      })
    );
    fechar();
  }

  const itens = useMemo(() => (Array.isArray(enderecos) ? enderecos : []), [enderecos]);

  const conteudo = (
    <>
      <Cabecalho>
        <Titulo id={`${id}-titulo`}>Onde você quer receber?</Titulo>
        {ehPainelInferior && (
          <BotaoFechar type="button" onClick={fechar} aria-label="Fechar">
            <FiX size={16} aria-hidden="true" />
          </BotaoFechar>
        )}
      </Cabecalho>

      {!signed && (
        <LinhaExplicativa>Informe seu CEP para ver o prazo de entrega</LinhaExplicativa>
      )}

      {signed && (
        <ListaEnderecos>
          {/* Sem este item o painel abre com a lista inteira desmarcada e o cliente não
              identifica qual é o local vigente. */}
          {cepAvulsoAtivo && (
            <ItemEndereco as="div" selecionado>
              <Radio selecionado aria-hidden="true" />
              <ItemTextos>
                <ItemTitulo>CEP informado</ItemTitulo>
                <ItemResumo>{formatCEP(cep)}</ItemResumo>
              </ItemTextos>
            </ItemEndereco>
          )}

          {itens.map((endereco, indice) => {
            const selecionado = endereco.id === enderecoId;
            return (
              <ItemEndereco
                key={endereco.id}
                type="button"
                selecionado={selecionado}
                aria-pressed={selecionado}
                ref={indice === 0 && !cepAvulsoAtivo ? primeiroItemRef : undefined}
                onClick={() => selecionarEndereco(endereco)}
              >
                <Radio selecionado={selecionado} aria-hidden="true" />
                <ItemTextos>
                  <ItemTitulo>{endereco.endereco}</ItemTitulo>
                  <ItemResumo>
                    {formatAddress(
                      endereco.endereco,
                      endereco.numero,
                      endereco.bairro,
                      endereco.cidade
                        ? `${endereco.cidade.nome} - ${endereco.cidade.uf_sigla}`
                        : "",
                      endereco.complemento
                    )}
                  </ItemResumo>
                </ItemTextos>
              </ItemEndereco>
            );
          })}
        </ListaEnderecos>
      )}

      {signed && <DivisorOu><span>ou</span></DivisorOu>}

      <CampoBloco>
        <RotuloCampo htmlFor={`${id}-cep`}>
          {signed ? "Informar outro CEP" : "Seu CEP"}
        </RotuloCampo>
        <LinhaCampo>
          <MaskedInput
            mask={cepMask}
            id={`${id}-cep`}
            value={valorCep}
            onChange={onChangeCep}
            onKeyDown={onKeyDownCep}
            placeholder="Ex: _____-___"
            aria-invalid={erroCep ? "true" : "false"}
            aria-describedby={erroCep ? `${id}-cep-erro` : undefined}
            render={(ref, props) => (
              <InputCep
                ref={(input) => {
                  ref(input);
                  inputRef.current = input;
                }}
                invalido={!!erroCep}
                {...props}
              />
            )}
          />
          <BotaoAplicar type="button" onClick={aplicarCep} disabled={aplicando}>
            {aplicando ? "..." : "Aplicar"}
          </BotaoAplicar>
        </LinhaCampo>
        {erroCep && <MensagemErro id={`${id}-cep-erro`}>{erroCep}</MensagemErro>}
      </CampoBloco>

      <RodapePainel>
        <LinkFarmacia type="button" onClick={() => setModalFarmaciaAberto(true)}>
          <FiHome size={16} aria-hidden="true" />
          Escolher uma farmácia específica
        </LinkFarmacia>
      </RodapePainel>

      <RegiaoStatus role="status" aria-live="polite">
        {anuncio}
      </RegiaoStatus>
    </>
  );

  const painel = ehPainelInferior ? (
    <>
      <Backdrop onClick={fechar} />
      <Painel
        ref={painelRef}
        id={id}
        inferior
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titulo`}
      >
        {conteudo}
      </Painel>
    </>
  ) : (
    // Popover: sem role de diálogo e sem aria-modal, de propósito.
    <Painel
      ref={painelRef}
      id={id}
      style={posicao ? { top: posicao.top, left: posicao.left } : { visibility: "hidden" }}
      aria-labelledby={`${id}-titulo`}
    >
      {conteudo}
    </Painel>
  );

  return createPortal(
    <>
      {painel}
      {modalFarmaciaAberto && (
        <SelectStoreModal closeModal={() => setModalFarmaciaAberto(false)} />
      )}
    </>,
    document.body
  );
}
